import { dayjs } from '../../support/helpers/dayjs.ts';
import { State } from '@autopylot-internal/state';
import { ICoordinate } from '../../support/model/ICoordinate.ts';
import { PaywallDialogViewModel } from '../../paywall/PaywallDialogViewModel.tsx';
import { IAsyncSmartStorageProvider, StorageProvider } from '../../support/StorageProvider.ts';
import { IPaymentTerm, RevenueCatModel } from '../../paywall/revenue-cat/RevenueCatModel.ts';
import { TrackingService } from '../../support/tracking/TrackingService.ts';
import { ApMapboxStylerTimeState } from '../../map/styles/ApMapboxStylerTimeState.ts';
import { MapViewModel } from '../../map/model/MapViewModel.ts';
import { MissionPinAndRadiusOnMapViewModel } from '../../map/mission-pin-and-radius/MissionPinAndRadiusOnMapViewModel.ts';
import { WindyMapViewModel } from './WindyMapViewModel.ts';

export const PAYWALL_COUNTDOWN_SECS = 5;
export const PAYWALL_PREVIEW_LIMIT = 3;
export const PAYWALL_RESET_DAYS = 30;

interface IWindyMapModalStorage {
    recentUsage: string[];
}

interface IWindyMapModalState {
    open: boolean;
    startTime?: string;
    center?: ICoordinate;
    zoom?: number;
    radiusMeters?: number;
    radiusColor?: string;
    radiusShape?: 'circle' | 'square';
    countdown: number;
}

export class WindyMapModalState extends State<IWindyMapModalState> {

    private storage: IAsyncSmartStorageProvider<IWindyMapModalStorage>;
    private countdownInterval: any;

    static inject = () => [
        PaywallDialogViewModel,
        RevenueCatModel,
        WindyMapViewModel,
        MapViewModel,
        ApMapboxStylerTimeState,
        MissionPinAndRadiusOnMapViewModel,
        TrackingService,
        StorageProvider,
    ];
    constructor(
        private paywall: PaywallDialogViewModel,
        private revenueCat: RevenueCatModel,
        private windyMap: WindyMapViewModel,
        private map: MapViewModel,
        private mapTime: ApMapboxStylerTimeState,
        private radius: MissionPinAndRadiusOnMapViewModel,
        private tracker: TrackingService,
        storageProvider: StorageProvider
    ) {
        super({ open: false, countdown: 0 });
        this.storage = storageProvider.getAsyncJSONProvider<IWindyMapModalStorage>('windy-modal');
    }

    private resetUsage = () => {
        this.storage.set({ recentUsage: [] });
    };

    private updateUsage = async () => {
        const { recentUsage = [] } = await this.storage.get() || {};
        const updatedUsage = [...recentUsage.filter(u => u > dayjs().subtract(PAYWALL_RESET_DAYS, 'd').toISOString()), dayjs().toISOString()];
        this.storage.set({ recentUsage: updatedUsage });
        return updatedUsage;
    };

    private getPreviewLimit = () => {
        const { windyPreviewsPerMonth = PAYWALL_PREVIEW_LIMIT } = this.revenueCat.getFreeTierLimits();
        return windyPreviewsPerMonth;
    };

    private startCountdown = (callback: () => void) => {
        let countdown = PAYWALL_COUNTDOWN_SECS + 1;
        this.countdownInterval = setInterval(() => {
            countdown--;
            this.setState({ countdown });
            if (countdown < 1) {
                this.stopCountdown();
                callback();
            }
        }, 1000);
    };

    private stopCountdown = () => {
        clearInterval(this.countdownInterval);
        this.setState({ countdown: 0 });
    };

    private showMap = () => {
        const radiusProps = this.radius.getRadius()?.properties;
        this.setState({
            open: true,
            startTime: this.mapTime.state.startTime,
            center: this.map.getCenter(),
            zoom: this.map.getZoom(),
            radiusMeters: radiusProps?.radiusMeters,
            radiusColor: radiusProps?.color,
            radiusShape: radiusProps?.isSquare ? 'square' : 'circle'
        });
    };

    showPaywall = async () => {
        const { center, radiusMeters } = this.state;
        const pinDropParam = center && radiusMeters ? `?dropPin=${center.lng},${center.lat}` : '';

        const upgraded = await this.paywall.requirePurchase({
            triggerSource: 'windy',
            returnTo: `/map${pinDropParam}`,
        });

        if (upgraded) {
            this.resetUsage();
            this.stopCountdown();
        }

        return upgraded;
    };

    isAvailable = () => {
        return this.windyMap.isForecastAvailable({ dateTime: this.mapTime.state.startTime, location: this.map.getCenter() });
    };

    open = async () => {
        let upgraded = this.revenueCat.hasUpgraded();

        if (upgraded) {
            this.resetUsage();

        } else {
            const updatedUsage = await this.updateUsage();
            if (updatedUsage.length > this.getPreviewLimit()) {
                upgraded = await this.showPaywall();
                if (!upgraded) {
                    return;
                }
            }

            if (!upgraded) {
                this.startCountdown(async () => {
                    if (this.state.open) {
                        upgraded = await this.showPaywall();
                        if (!upgraded) {
                            this.close();
                            return;
                        }
                    }
                });
            }
        }

        this.showMap();

        this.tracker.track('Mission Planning Weather Map Opened', () => ({
            hasPin: Boolean(this.radius.getRadius()?.properties?.radiusMeters)
        }));
    };

    resumePaywall = async ({ paymentTerm }: { paymentTerm: IPaymentTerm }) => {
        let upgraded = this.revenueCat.hasUpgraded();

        if (upgraded) {
            this.resetUsage();
        }

        // we can get here before the map is initialized
        await this.map.asyncInitialized();

        this.showMap();

        if (!upgraded) {
            upgraded = await this.paywall.resume({ paymentTerm });
            if (upgraded) {
                this.resetUsage();
            } else {
                this.close();
            }
        }
    };

    close = () => {

        this.stopCountdown();

        this.setState({
            open: false
        });

    };
}
